import { makeStyles } from '@material-ui/core'

/**
 * 3-blade dialog layout for the Groupings & Filters dialog (SMT-1608
 * Phase 1). Mirrors the column-builder dialog's structure (catalog +
 * selected list + detail panel) so the two configuration surfaces feel
 * familiar to users who've already used Column Builder.
 */
const useStyles = makeStyles(() => ({
  dialogLayout: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0
  },
  body: {
    display: 'flex',
    gap: 16,
    maxHeight: 'calc(100vh - 300px)',
    minHeight: 360,
    overflow: 'hidden'
  },
  // --- Blade 1: catalog of available groupings ---
  catalogPanel: {
    flex: '0 0 200px',
    display: 'flex',
    flexDirection: 'column',
    borderRight: '1px solid var(--opal-border-default, #e0e0e0)',
    paddingRight: 16,
    overflowY: 'auto',
    minHeight: 0
  },
  // --- Blade 2: selected groupings (drag-to-reorder, click-to-focus) ---
  selectedPanel: {
    flex: '1 1 auto',
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
    minHeight: 0
  },
  // --- Blade 3: detail (filter builder for the focused grouping) ---
  detailPanel: {
    flex: '0 0 460px',
    display: 'flex',
    flexDirection: 'column',
    borderLeft: '1px solid var(--opal-border-default, #e0e0e0)',
    paddingLeft: 16,
    overflowY: 'auto',
    minHeight: 0
  },
  panelHeader: {
    fontSize: 11,
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    color: 'var(--color-neutral-600, #666)',
    marginBottom: 8
  },
  // --- catalog list items ---
  catalogList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2
  },
  catalogItem: {
    display: 'flex',
    alignItems: 'center',
    fontSize: 13,
    padding: '5px 8px',
    borderRadius: 'var(--border-radius-sm, 4px)',
    cursor: 'pointer',
    userSelect: 'none',
    '&:hover': {
      background: 'var(--color-primary-50, #f0f7ff)',
      color: 'var(--color-primary-700, #1565c0)'
    }
  },
  catalogItemAdded: {
    color: 'var(--color-neutral-400, #aaa)',
    cursor: 'default',
    '&:hover': {
      background: 'transparent',
      color: 'var(--color-neutral-400, #aaa)'
    }
  },
  // --- selected list ---
  selectedList: {
    flex: 1,
    overflow: 'auto',
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    border: '1px solid var(--opal-border-default, #e0e0e0)',
    borderRadius: 'var(--border-radius-sm, 4px)',
    padding: 4,
    minHeight: 100
  },
  groupingRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '7px 8px',
    borderRadius: 'var(--border-radius-sm, 4px)',
    border: '1px solid var(--opal-border-default, #e0e0e0)',
    background: 'var(--theme-color-surface-d0, #fff)',
    fontSize: 13,
    cursor: 'pointer',
    userSelect: 'none',
    '&:hover': {
      background: 'var(--color-neutral-100, #f0f0f0)'
    }
  },
  groupingRowSelected: {
    background: 'var(--color-primary-50, #f0f7ff)',
    outline: '1px solid var(--color-primary-200, #90caf9)',
    '&:hover': {
      background: 'var(--color-primary-50, #f0f7ff)'
    }
  },
  groupingRowDragging: {
    opacity: 0.4
  },
  dragHandle: {
    color: 'var(--color-neutral-400, #aaa)',
    flexShrink: 0,
    cursor: 'grab'
  },
  depthIndicator: {
    fontSize: 11,
    color: 'var(--color-neutral-500, #888)',
    minWidth: 18,
    textAlign: 'center'
  },
  groupingLabel: {
    flex: 1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    fontWeight: 500
  },
  filterBadge: {
    fontSize: 11,
    fontWeight: 500,
    color: 'var(--color-primary-700, #1565c0)',
    background: 'var(--color-primary-100, #e3f2fd)',
    borderRadius: 999,
    padding: '1px 8px',
    flexShrink: 0
  },
  filterBadgeEmpty: {
    fontSize: 11,
    color: 'var(--color-neutral-400, #aaa)',
    flexShrink: 0
  },
  removeBtn: {
    cursor: 'pointer',
    color: 'var(--color-neutral-400, #aaa)',
    flexShrink: 0,
    '&:hover': {
      color: 'var(--color-danger-500, #d32f2f)'
    }
  },
  emptyHint: {
    color: 'var(--color-neutral-400, #aaa)',
    fontSize: 12,
    fontStyle: 'italic',
    padding: 8,
    textAlign: 'center'
  },
  // --- bottom row of selected blade (benchmarks switch lives here today) ---
  optionsRow: {
    marginTop: 12,
    paddingTop: 12,
    paddingBottom: 16,
    borderTop: '1px solid var(--opal-border-default, #e0e0e0)',
    display: 'flex',
    flexDirection: 'column',
    gap: 4
  },
  optionLabel: {
    '& .MuiFormControlLabel-label': { fontSize: 13 }
  },
  optionHint: {
    fontSize: 11,
    lineHeight: 1.4,
    color: 'var(--color-neutral-500, #888)',
    marginLeft: 48
  },
  // --- detail panel (filter builder host) ---
  detailTitle: {
    fontSize: 15,
    fontWeight: 600,
    marginBottom: 2,
    color: 'var(--color-neutral-800, #333)'
  },
  detailSubtitle: {
    fontSize: 12,
    color: 'var(--color-neutral-500, #888)',
    marginBottom: 16
  },
  detailEmpty: {
    fontSize: 13,
    color: 'var(--color-neutral-500, #888)',
    fontStyle: 'italic',
    padding: 24,
    textAlign: 'center'
  },
  // --- filter builder ---
  collection: {
    border: '1px solid var(--opal-border-default, #e0e0e0)',
    borderRadius: 'var(--border-radius-sm, 4px)',
    padding: 8,
    marginBottom: 8
  },
  collectionNested: {
    background: 'var(--color-neutral-50, #fafafa)',
    marginLeft: 12,
    marginTop: 6,
    marginBottom: 6
  },
  collectionHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 8
  },
  methodToggle: {
    display: 'inline-flex',
    border: '1px solid var(--color-neutral-300, #ddd)',
    borderRadius: 'var(--border-radius-sm, 4px)',
    overflow: 'hidden'
  },
  methodOption: {
    fontSize: 12,
    fontWeight: 500,
    padding: '4px 12px',
    cursor: 'pointer',
    userSelect: 'none',
    background: 'var(--theme-color-surface-d0, #fff)',
    color: 'var(--color-neutral-700, #555)',
    '&:hover': {
      background: 'var(--color-neutral-100, #f0f0f0)'
    }
  },
  methodOptionActive: {
    background: 'var(--color-primary-50, #f0f7ff)',
    color: 'var(--color-primary-700, #1565c0)'
  },
  predicateRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 6,
    padding: '6px 4px',
    flexWrap: 'wrap'
  },
  predicateRowMain: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    flex: 1,
    minWidth: 0,
    flexWrap: 'wrap'
  },
  predicateField: {
    fontSize: 13,
    padding: '4px 6px',
    border: 'var(--border-neutral-thin, 1px solid #ddd)',
    borderRadius: 'var(--border-radius-sm, 4px)',
    background: 'var(--theme-color-surface-d0, #fff)',
    color: 'inherit',
    outline: 'none',
    minWidth: 100,
    '&:focus': {
      outline: '1px solid var(--color-primary-500, #1976d2)'
    }
  },
  presetChip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '4px 10px',
    background: 'var(--color-primary-100, #e3f2fd)',
    color: 'var(--color-primary-800, #0d47a1)',
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 500
  },
  addRow: {
    display: 'flex',
    gap: 6,
    marginTop: 8,
    flexWrap: 'wrap'
  },
  addBtn: {
    fontSize: 12,
    fontWeight: 500,
    padding: '4px 10px',
    border: '1px dashed var(--color-neutral-300, #ccc)',
    borderRadius: 'var(--border-radius-sm, 4px)',
    background: 'transparent',
    color: 'var(--color-primary-700, #1565c0)',
    cursor: 'pointer',
    '&:hover': {
      background: 'var(--color-primary-50, #f0f7ff)',
      borderColor: 'var(--color-primary-400, #3a83f5)'
    }
  },
  advancedToggle: {
    fontSize: 11,
    color: 'var(--color-neutral-500, #888)',
    cursor: 'pointer',
    userSelect: 'none',
    marginTop: 4,
    '&:hover': {
      color: 'var(--color-primary-700, #1565c0)'
    }
  },
  advancedPanel: {
    marginTop: 6,
    padding: 8,
    background: 'var(--color-neutral-50, #fafafa)',
    borderRadius: 'var(--border-radius-sm, 4px)',
    display: 'flex',
    flexDirection: 'column',
    gap: 6
  },
  advancedField: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    fontSize: 12
  }
}))

export default useStyles
